import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import {
  Backdrop,
  Banner,
  ButtonPrimary,
  ButtonTertiary,
  DialogAlert,
  DialogConfirmation,
  EmptyState,
  EmptyStateButton,
  EmptyStateText,
  EmptyStateTitle,
  FormCheckbox,
  FormHelpText,
  FormGroup,
  FormRadio,
  IconOffline,
  Type,
} from '@spotify-internal/encore-web';
import { AdsBox, Stack } from '@spotify-internal/encore-web/ads-business';
import { useFocusTrap } from '@spotify-internal/encore-web/content-platform';
import { useT } from '@spotify-internal/i18n-core';
import { sendEvent } from '@spotify-internal/s4mp-page-analytics';
import { Resource } from '@hooks/useCurrentUserInfo/types';
import { useHeldReleasesRequest } from '@hooks/useHeldReleases';
import { useModalPopoverFocus } from '@hooks/useModalPopoverFocus';
import { ArtistRelocationModal } from '@components/Artist/ArtistRelocationModal';
import { EVENT_ACTION, EVENT_CATEGORY } from '@lib/ga/types';
import { GA_PRODUCT_CATALOG } from '@helpers/constants';
import type { AlbumMetadata } from '@generated/grpc/spotify/artistwatchlist/v1';
import { APPROVAL_REASON, getReasonLabels } from './constants';

const DialogAlertWide = styled(DialogAlert)`
  inline-size: 500px;
`;

export const HeldReleasesRequestModal = ({
  requestedRelease,
  licensor,
  onClose,
  refresh,
}: {
  requestedRelease: AlbumMetadata;
  licensor?: Resource;
  onClose: () => void;
  refresh: () => void;
}) => {
  const [checkBoxSelected, toggleCheckBoxSelected] = useState(false);
  const [showLegalCheckBoxError, toggleShowLegalCheckBoxError] =
    useState(false);
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [reason, setReason] = useState<APPROVAL_REASON>(
    APPROVAL_REASON.RIGHTS_TO_RELEASE,
  );
  const [showArtistRelocationModal, toggleShowArtistRelocationModal] =
    useState(false);
  const { triggerBtnRef, toggleModal } = useModalPopoverFocus(
    showArtistRelocationModal,
  );

  const { trigger, error } = useHeldReleasesRequest();
  const firstInteractiveEleRef = useRef<HTMLElement>(null);
  const lastInteractiveEleRef = useRef<HTMLElement>(null);
  useEffect(() => {
    if (firstInteractiveEleRef.current) {
      firstInteractiveEleRef.current?.focus();
    }
  }, [firstInteractiveEleRef]);
  useFocusTrap(firstInteractiveEleRef, lastInteractiveEleRef);

  const onSendApprovalRequest = async () => {
    if (checkBoxSelected) {
      toggleShowLegalCheckBoxError(false);
      await trigger({
        id: 0,
        releaseUri: requestedRelease.albumUri,
        approvalDate: {
          seconds: Math.floor(new Date().getTime() / 1000),
        },
        licensorName: requestedRelease.licensorName,
        watchedArtist: requestedRelease.blockingArtist?.artistName,
        submissionType: reason,
        licensorId: licensor?.id,
      });

      sendEvent({
        eventCategory: EVENT_CATEGORY.HELD_RELEASES_MODAL,
        eventAction: EVENT_ACTION.CLICK,
        eventLabel: `Held Releases Form Submitted`,
        product: GA_PRODUCT_CATALOG,
      });
      setHasSubmitted(true);
      refresh();
    } else {
      toggleShowLegalCheckBoxError(true);
    }
  };

  const handleBackToCatalogClickGaEvent = () => {
    sendEvent({
      eventCategory: EVENT_CATEGORY.HELD_RELEASES_MODAL,
      eventAction: EVENT_ACTION.CLICK,
      eventLabel: 'Held Releases Form Completed',
      product: GA_PRODUCT_CATALOG,
    });
  };

  const t = useT();
  const REASON_LABEL = getReasonLabels(t);

  const getTitle = useCallback(() => {
    if (hasSubmitted) {
      return t(
        'catalog.on_held.request_modal.title_confirm',
        'Request confirmation',
        'Title for on held request approval modal after submitting it',
      );
    }

    return t(
      'catalog.on_held.request_modal.title',
      'Request approval',
      'Title for on held request approval modal',
    );
  }, [t, hasSubmitted]);

  const getSubTitle = useCallback(() => {
    if (hasSubmitted) {
      return t(
        'catalog.on_held.request_modal.subtitle_confirm',
        'Your request has been submitted. Products can take up to 5 business days to go live.',
        'Subtitle for on held request approval modal after submitting it',
      );
    }

    return t(
      'catalog.on_held.request_modal.subtitle',
      'This product is held in review due to this artist: {artistName}',
      'Subtitle for on held request approval modal',
      { artistName: requestedRelease.blockingArtist?.artistName },
    );
  }, [t, hasSubmitted, requestedRelease.blockingArtist?.artistName]);

  const getReasonSection = () => {
    if (hasSubmitted) {
      return <Type variant="mesto">{REASON_LABEL[reason]}</Type>;
    }

    return (
      <>
        <FormRadio
          id={APPROVAL_REASON.RIGHTS_TO_RELEASE}
          name="reason"
          defaultChecked
          onChange={() => setReason(APPROVAL_REASON.RIGHTS_TO_RELEASE)}
          ref={
            reason === APPROVAL_REASON.RIGHTS_TO_RELEASE
              ? firstInteractiveEleRef
              : undefined
          }
        >
          {REASON_LABEL[APPROVAL_REASON.RIGHTS_TO_RELEASE]}
        </FormRadio>
        <FormRadio
          id={APPROVAL_REASON.RIGHTS_TO_ARTIST}
          name="reason"
          onChange={() => setReason(APPROVAL_REASON.RIGHTS_TO_ARTIST)}
          ref={
            reason === APPROVAL_REASON.RIGHTS_TO_ARTIST
              ? firstInteractiveEleRef
              : undefined
          }
        >
          {REASON_LABEL[APPROVAL_REASON.RIGHTS_TO_ARTIST]}
        </FormRadio>
        <FormRadio
          id={APPROVAL_REASON.NOT_SAME_ARTIST}
          name="reason"
          onClick={() => {
            sendEvent({
              eventCategory: EVENT_CATEGORY.HELD_RELEASES_MODAL,
              eventAction: EVENT_ACTION.CLICK,
              eventLabel: `Held Releases Form: Not the same artist`,
              product: GA_PRODUCT_CATALOG,
            });
          }}
          ref={
            reason === APPROVAL_REASON.NOT_SAME_ARTIST
              ? firstInteractiveEleRef
              : undefined
          }
          onChange={() => setReason(APPROVAL_REASON.NOT_SAME_ARTIST)}
        >
          {REASON_LABEL[APPROVAL_REASON.NOT_SAME_ARTIST]}
        </FormRadio>
        <FormRadio
          id={APPROVAL_REASON.SHOULD_NOT_GO_LIVE}
          name="reason"
          onChange={() => setReason(APPROVAL_REASON.SHOULD_NOT_GO_LIVE)}
          ref={
            reason === APPROVAL_REASON.SHOULD_NOT_GO_LIVE
              ? firstInteractiveEleRef
              : undefined
          }
        >
          {REASON_LABEL[APPROVAL_REASON.SHOULD_NOT_GO_LIVE]}
        </FormRadio>
      </>
    );
  };

  const getBannerSection = () => {
    if (hasSubmitted) {
      switch (reason) {
        case APPROVAL_REASON.NOT_SAME_ARTIST:
          return (
            <Banner>
              <span
                dangerouslySetInnerHTML={{
                  __html: t(
                    'catalog.on_held.request_modal.not_same_artist_banner',
                    'You need to fill out a Content Relocation form. Fill out the form and our Metadata team will get in touch. If you don\'t fill out the form, your product will remain held in review. Take a look at <a rel="noopener noreferrer" href="https://providersupport.spotify.com/subsection/content-relocation">this article</a> for help with the form.',
                    'On held request for approval banner warning users to submit artist approval form',
                  ),
                }}
              />
            </Banner>
          );

        case APPROVAL_REASON.RIGHTS_TO_RELEASE:
        case APPROVAL_REASON.RIGHTS_TO_ARTIST:
        case APPROVAL_REASON.SHOULD_NOT_GO_LIVE:
        default:
          return <></>;
      }
    }

    return <></>;
  };

  const getFooterSection = () => {
    if (hasSubmitted) {
      switch (reason) {
        case APPROVAL_REASON.NOT_SAME_ARTIST:
          return (
            <div>
              <ButtonTertiary
                buttonSize="sm"
                condensed
                onClick={() => {
                  handleBackToCatalogClickGaEvent();
                  onClose();
                }}
              >
                {t(
                  'catalog.on_held.request_modal.back_catalog',
                  'Back to Catalog',
                  'On held request for approval back to catalog action',
                )}
              </ButtonTertiary>
              <ButtonPrimary
                buttonSize="sm"
                onClick={() => {
                  toggleShowArtistRelocationModal(true);
                }}
              >
                {t(
                  'catalog.on_held.request_modal.content_relocation_form_label',
                  'Go to content relocation form',
                  'On held request for approval button label for content relocation form',
                )}
              </ButtonPrimary>
            </div>
          );

        case APPROVAL_REASON.RIGHTS_TO_RELEASE:
        case APPROVAL_REASON.RIGHTS_TO_ARTIST:
        case APPROVAL_REASON.SHOULD_NOT_GO_LIVE:
        default:
          return (
            <div>
              <ButtonPrimary
                buttonSize="sm"
                onClick={() => {
                  handleBackToCatalogClickGaEvent();
                  onClose();
                }}
              >
                {t(
                  'catalog.on_held.request_modal.back_catalog',
                  'Back to Catalog',
                  'On held request for approval back to catalog action',
                )}
              </ButtonPrimary>
            </div>
          );
      }
    }

    return (
      <div>
        <ButtonTertiary
          buttonSize="sm"
          condensed
          onClick={() => {
            sendEvent({
              eventCategory: EVENT_CATEGORY.HELD_RELEASES_MODAL,
              eventAction: EVENT_ACTION.CLICK,
              eventLabel: `Held Releases Form Cancelled`,
              product: GA_PRODUCT_CATALOG,
            });
            onClose();
          }}
        >
          {t('atlas.actions.cancel', 'Cancel', 'Cancel the current action.')}
        </ButtonTertiary>
        <ButtonPrimary
          buttonSize="sm"
          onClick={onSendApprovalRequest}
          ref={lastInteractiveEleRef}
        >
          {t(
            'catalog.on_held.request_modal.submit',
            'Submit request',
            'On held request for approval submit',
          )}
        </ButtonPrimary>
      </div>
    );
  };

  const onRequestApprovalAgain = () => {
    setHasSubmitted(false);
    toggleCheckBoxSelected(false);
    toggleShowLegalCheckBoxError(false);
  };

  if (error && hasSubmitted) {
    return (
      <Backdrop center onClose={onClose}>
        <DialogAlertWide
          aria-label="Title"
          id="dialogAlert"
          body={
            <EmptyState
              icon={IconOffline}
              maxInlineSize="600px"
              iconColorSet="negative"
            >
              <EmptyStateTitle>
                {t(
                  'catalog.on_held.request_modal.error_title',
                  'Request failed',
                  'On held request for approval error title',
                )}
              </EmptyStateTitle>
              <EmptyStateText>
                {t(
                  'catalog.on_held.request_modal.error_description',
                  "There's a problem on our end. Try requesting approval again or in a few minutes.",
                  'On held request for approval error description',
                )}
              </EmptyStateText>
              <EmptyStateButton onClick={onRequestApprovalAgain}>
                {t(
                  'catalog.on_held.request_modal.error_try_again_action',
                  'Request approval again',
                  'On held request for approval error try again action',
                )}
              </EmptyStateButton>
            </EmptyState>
          }
        />
      </Backdrop>
    );
  }

  if (showArtistRelocationModal) {
    return (
      <ArtistRelocationModal
        firstBtnRef={firstInteractiveEleRef}
        lastBtnRef={lastInteractiveEleRef}
        onClose={() => {
          toggleShowArtistRelocationModal(false);
          toggleModal();
          triggerBtnRef.current?.focus();
          onClose();
        }}
        uri={`${requestedRelease.albumUri}`}
        additionalComment="Request coming from held in review tab"
      />
    );
  }

  return (
    <Backdrop center onClose={onClose}>
      <DialogConfirmation
        dialogTitle={getTitle()}
        body={
          <Stack>
            <Type as="p">{getSubTitle()}</Type>
            <AdsBox boxSize="sm">
              <Type variant="ballad">{requestedRelease.albumName}</Type>
              <Type variant="mesto" semanticColor="textSubdued">
                {requestedRelease.artists
                  ?.map(artist => artist.artistName)
                  .join(', ') || ''}
              </Type>
            </AdsBox>
            <FormGroup
              label={t(
                'catalog.on_held.request_modal.reason_label',
                'Reason for approval',
                'On held reason for approval label for radio inputs',
              )}
              withFieldset
            >
              {getReasonSection()}
            </FormGroup>
            {getBannerSection()}
          </Stack>
        }
        legal={
          !hasSubmitted && (
            <>
              <FormCheckbox
                id="on-held-legal-option"
                aria-label="on-held-legal-option"
                onClick={() => {
                  toggleCheckBoxSelected(!checkBoxSelected);
                  toggleShowLegalCheckBoxError(false);
                }}
              >
                <Type variant="finale">
                  {t(
                    'catalog.on_held.request_modal.confirmation',
                    'Select the checkbox to confirm you understand that once approved, products can take up to 5 business days to go live, as stated in the Spotify Service Level Guideline.',
                    'On held request approval confirmation',
                  )}
                </Type>
              </FormCheckbox>
              {showLegalCheckBoxError && (
                <FormHelpText error>
                  {t(
                    'catalog.on_held.request_modal.checkbox_error',
                    'Select the checkbox to continue',
                    'Error to show when user has not selected legal checkbox',
                  )}
                </FormHelpText>
              )}
            </>
          )
        }
        legalStrict={!hasSubmitted}
        footer={getFooterSection()}
      />
    </Backdrop>
  );
};

export default HeldReleasesRequestModal;
